import { Injectable, NgZone } from '@angular/core';

/**
 * Cross-table visual linking for the v2 Data Categorization page.
 *
 * The Document Details table (source rows) and the Financial Template table
 * render the *same* categorized `Row` objects, so a row can be matched across
 * both panes by its `uuid` (exposed as `data-row-key`). This service is the
 * single home for the shared DOM behaviors that link the two tables:
 *
 *  - `setHoveredKey`  — bidirectional hover highlight (reuses the tables'
 *    existing `:hover` styling via the `.row-linked-hover` class).
 *  - `scrollRowIntoView` — bring a matching row into view (no highlight).
 *  - `flashRow` — scroll to a row and highlight it green for 3s (used only by
 *    the "View →" action in the categorization/validation tooltip, which
 *    reveals the categorized row's twin in the Financial Template table).
 *
 * All DOM work runs via `NgZone.runOutsideAngular`, so hover never triggers
 * change detection on these large grids.
 */
@Injectable({ providedIn: 'root' })
export class SpreadingRowLinkService {
  private static readonly HOVER_CLASS = 'row-linked-hover';
  private static readonly FLASH_CLASS = 'row-linked-flash';
  private static readonly FLASH_DURATION_MS = 3000;
  private static readonly SCROLL_OPTIONS: ScrollIntoViewOptions = {
    block: 'center',
    inline: 'nearest',
    behavior: 'smooth',
  };

  private _hoveredKey: string | null = null;
  private _flashedElement: HTMLElement | null = null;
  private _flashTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private ngZone: NgZone) {}

  /**
   * Highlight every row matching `key` across both tables, clearing any
   * previously highlighted rows. Passing `null` clears the highlight.
   */
  setHoveredKey(key: string | null): void {
    if (key === this._hoveredKey) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this._toggleHoverClass(this._hoveredKey, false);
      this._hoveredKey = key;
      this._toggleHoverClass(key, true);
    });
  }

  /** Smoothly scroll the first element matching `selector` into view. */
  scrollRowIntoView(selector: string): void {
    this.ngZone.runOutsideAngular(() => {
      const element = document.querySelector<HTMLElement>(selector);
      element?.scrollIntoView(SpreadingRowLinkService.SCROLL_OPTIONS);
    });
  }

  /**
   * Scroll the first element matching `selector` into view and flash it green
   * for 3 seconds. Any in-progress flash is cleared first. Pass `root` to scope
   * the lookup to a single table, since a categorized row carries the same
   * `data-row-key` in both.
   */
  flashRow(selector: string, root: ParentNode = document): void {
    this.ngZone.runOutsideAngular(() => {
      const element = root.querySelector<HTMLElement>(selector);
      if (!element) {
        return;
      }
      this._clearFlash();
      element.scrollIntoView(SpreadingRowLinkService.SCROLL_OPTIONS);
      // Restart the CSS animation explicitly. When the row is already in view,
      // scrollIntoView is a no-op, so nothing forces the reflow needed to
      // re-trigger the animation. Removing the class, flushing layout, then
      // re-adding it guarantees the flash replays regardless of scroll.
      element.classList.remove(SpreadingRowLinkService.FLASH_CLASS);
      void element.offsetWidth;
      element.classList.add(SpreadingRowLinkService.FLASH_CLASS);
      this._flashedElement = element;
      this._flashTimeoutId = setTimeout(
        () => this._clearFlash(),
        SpreadingRowLinkService.FLASH_DURATION_MS
      );
    });
  }

  private _toggleHoverClass(key: string | null, add: boolean): void {
    if (!key) {
      return;
    }
    const nodes = document.querySelectorAll<HTMLElement>(`[data-row-key="${key}"]`);
    nodes.forEach((node) =>
      node.classList.toggle(SpreadingRowLinkService.HOVER_CLASS, add)
    );
  }

  private _clearFlash(): void {
    if (this._flashTimeoutId !== null) {
      clearTimeout(this._flashTimeoutId);
      this._flashTimeoutId = null;
    }
    if (this._flashedElement) {
      this._flashedElement.classList.remove(SpreadingRowLinkService.FLASH_CLASS);
      this._flashedElement = null;
    }
  }
}
